import { headers as defaultHeaders, logPrefix } from './constants';
import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import Identity from './identity';
import { UserIdentity, isUserIdentity } from './userIdentity';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';

/**
 *
 * Upon successful token exchange with the IDP, this API will register/authenticate the user and respond with Arc XP's access and refresh tokens.
 *
 * @param {Object} props OIDC client-side PKCE properties
 *
 * @param {Sting} props.client_id Client ID registered with Arc XP OIDC Auth Provider in the Subscriptions settings.
 *
 * @param {Sting} props.code_verifier Original decrypted code used as the PKCE flow
 *
 * @param {Sting} props.redirect_uri Uri that you want to redirect to
 *
 * @param {Sting} props.code Code used to verify the user was signed in from the Arc XP org application.
 *
 * @returns
 * A __Promise__ with the user information. When successful, `userIdentity` will be populated with the data fetched by this method.
 *
 */

interface Props {
  client_id: string;
  code_verifier: string;
  redirect_uri: string;
  code: string;
}

interface OIDCToken {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
}

export function isOIDCToken(object: any): object is OIDCToken {
  return (<OIDCToken>object).access_token !== undefined;
}

export default async function signInWithOIDCPKCE({
  client_id,
  code_verifier,
  redirect_uri,
  code
}: Props): Promise<APIErrorResponse | UserIdentity> {
  const headers: { [index: string]: any } = {
    ...defaultHeaders
  };

  if (
    Identity.userIdentity &&
    Identity.userIdentity.accessToken &&
    Identity.userIdentity.refreshToken
  ) {
    await Identity.heartbeat();
    headers['Authorization'] = `Bearer ${Identity.userIdentity.accessToken}`;
  }

  const formData = new FormData();

  formData.append('grant_type', 'authorization_code');
  formData.append('client_id', client_id);
  formData.append('code_verifier', code_verifier);
  formData.append('redirect_uri', redirect_uri);
  formData.append('code', code);

  try {
    const response = await fetch(`${Identity.apiOrigin}/identity/public/v2/oidc/provider/token`, {
      method: 'POST',
      headers: new Headers(),
      body: formData
    }).then(JSONResponseHandler);

    if (isOIDCToken(response)) {
      Identity.userIdentity = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        idToken: response.id_token,
        expiresIn: response.expires_in
      };

      window.postMessage({ type: 'ARC_LOGIN_SUCCESS', grantType: 'OIDC' }, '*');
    } else {
      throw response;
    }

    const identityTokens = await fetch(`${Identity.apiOrigin}/identity/public/v1/auth/token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        token: response.refresh_token,
        grantType: 'refresh-token'
      })
    }).then(JSONResponseHandler);

    if (isUserIdentity(identityTokens)) {
      const userIdentity = {
        ...Identity.userIdentity,
        uuid: identityTokens.uuid,
        accessToken: identityTokens.accessToken,
        refreshToken: identityTokens.refreshToken || Identity.userIdentity.refreshToken
      };
      Identity.userIdentity = userIdentity;
      return Identity.userIdentity;
    } else {
      if (identityTokens.code) {
        console.warn(
          `${logPrefix} code:"${identityTokens.code}", message:"${identityTokens.message}"`
        );
      }
      Identity.clearSession();
      throw identityTokens;
    }
  } catch (err) {
    throw err;
  }
}
